import type { FC } from "react";
import { useId, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { Alert } from "@privacy/ui";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { ConfirmDeleteDialog } from "../../../../shared/ConfirmDeleteDialog";
import * as FormDialog from "../../../../shared/FormDialog";
import type { OrganisationHierarchy } from "../../../../api/organisations.types";
import { deleteTenantMutationOptions, tenantConnectedEntitiesQueryOptions } from "../../../../api/tenants";
import { ChevronRightIcon } from "../../../../shared/icons";
import { flattenTenantHierarchy } from "../../../../shared/TenantSelect";
import { customScrollbar } from "../../../../shared/customScrollbar";
import { ErrorMessage } from "../../../../dynamicForm/inlineForm/ErrorMessage";

const CONNECTED_ENTITY_KEYS = [
  "registrations",
  "processorRegistrations",
  "dataProtectionImpactAssessments",
  "preAssessments",
  "partners",
] as const;

type ConnectedEntityKey = (typeof CONNECTED_ENTITY_KEYS)[number];

const CONNECTED_ENTITY_LABELS: Record<ConnectedEntityKey, TranslationKey> = {
  registrations: "OrganisationStructure.DeleteConfirmation.ConnectedEntities.Registrations",
  processorRegistrations: "OrganisationStructure.DeleteConfirmation.ConnectedEntities.ProcessorRegistrations",
  dataProtectionImpactAssessments: "OrganisationStructure.DeleteConfirmation.ConnectedEntities.Dpias",
  preAssessments: "OrganisationStructure.DeleteConfirmation.ConnectedEntities.PreAssessments",
  partners: "OrganisationStructure.DeleteConfirmation.ConnectedEntities.Partners",
};

const CHEVRON_SIZE = 14;
const ROW_PADDING_LEFT = 2;
const ROW_GAP = 1;
const COUNT_PADDING_RIGHT = 4;

const pluralSuffix = (count: number) => (count === 1 ? "Singular" : "Plural");

const formatCount = (count: number, language: string) => count.toLocaleString(language);

const summaryTextKey = (hasRows: boolean, subTenantCount: number) => {
  if (hasRows && subTenantCount === 0) return "OrganisationStructure.DeleteConfirmation.ConnectedEntities.Summary";
  if (hasRows)
    return `OrganisationStructure.DeleteConfirmation.ConnectedEntities.SummaryWithSubTenants.${pluralSuffix(subTenantCount)}` as const;
  if (subTenantCount > 0)
    return `OrganisationStructure.DeleteConfirmation.ConnectedEntities.SubTenantsOnly.${pluralSuffix(subTenantCount)}` as const;
  return null;
};

type ConfirmDeleteTenantDialogProps = {
  tenant: OrganisationHierarchy;
  hasConnectedEntities: (tenant: OrganisationHierarchy) => boolean;
  onClose: () => void;
};

export const ConfirmDeleteTenantDialog: FC<ConfirmDeleteTenantDialogProps> = ({
  tenant,
  hasConnectedEntities,
  onClose,
}) => {
  const { t } = useTranslation();
  const deleteTenantMutation = useMutation(deleteTenantMutationOptions());

  const hasConnections = hasConnectedEntities(tenant);

  const handleDelete = () => deleteTenantMutation.mutateAsync(tenant.id, { onSuccess: onClose });

  const translationKeyPrefix = hasConnections
    ? "OrganisationStructure.DeleteConfirmation"
    : "OrganisationStructure.DeleteConfirmation.Simple";

  return (
    <ConfirmDeleteDialog
      isOpen
      id="delete-tenant-dialog"
      onDelete={handleDelete}
      onClose={onClose}
      headerText={t(`${translationKeyPrefix}.Title`)}
      message={
        <Translate
          translationKey={`${translationKeyPrefix}.Message`}
          values={{
            name: (
              <Box component="span" sx={{ fontWeight: "bold" }}>
                {tenant.organizationName}
              </Box>
            ),
          }}
        />
      }
      details={<ConnectedEntitiesSection tenant={tenant} />}
      errorMessage={
        deleteTenantMutation.isError ? t("OrganisationStructure.DeleteConfirmation.DeleteError") : undefined
      }
      confirmButtonLabel={t(`${translationKeyPrefix}.Submit`)}
      variant={hasConnections ? "severe" : "standard"}
      sx={{
        "& .MuiDialog-container": { alignItems: "flex-start" },
        "& .MuiDialog-paper": { mt: "8vh", maxHeight: "calc(100% - 8vh - 32px)" },
      }}
    />
  );
};

const ConnectedEntitiesSection: FC<{ tenant: OrganisationHierarchy }> = ({ tenant }) => {
  const { t, language } = useTranslation();
  const connectedEntitiesQuery = useQuery(tenantConnectedEntitiesQueryOptions(tenant.id));
  const [openKey, setOpenKey] = useState<ConnectedEntityKey | null>(null);

  if (connectedEntitiesQuery.isPending) {
    return <Skeleton variant="rounded" height={72} sx={{ mb: 4 }} />;
  }

  if (connectedEntitiesQuery.isError) {
    return (
      <Typography
        sx={{ mb: 4, fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.error.main }}
      >
        {t("OrganisationStructure.DeleteConfirmation.ConnectedEntities.Error")}
      </Typography>
    );
  }

  const connectedEntities = connectedEntitiesQuery.data;
  const keysWithEntities = CONNECTED_ENTITY_KEYS.filter(key => connectedEntities[key] > 0);

  if (
    keysWithEntities.length === 0 &&
    connectedEntities.subTenants === 0 &&
    connectedEntities.sharedRegistrations === 0 &&
    connectedEntities.registrationsLosingDpiaConnection === 0
  ) {
    return null;
  }

  const units = flattenTenantHierarchy(tenant);
  const entitiesByTenantId = new Map(
    connectedEntities.tenants.map(tenantEntities => [tenantEntities.tenantId, tenantEntities])
  );

  const unitsFor = (key: ConnectedEntityKey) => {
    const unitCounts = units.map(unit => ({
      id: unit.id,
      name: unit.organizationName,
      count: entitiesByTenantId.get(unit.id)?.[key] ?? 0,
    }));
    return [...unitCounts.filter(unit => unit.count > 0), ...unitCounts.filter(unit => unit.count === 0)];
  };

  const summaryKey = summaryTextKey(keysWithEntities.length > 0, connectedEntities.subTenants);

  return (
    <Box sx={{ mb: 4 }}>
      {summaryKey && (
        <Typography
          sx={{
            mb: keysWithEntities.length > 0 ? 2 : 0,
            fontSize: theme => theme.typography.pxToRem(14),
            color: theme => theme.palette.text.secondary,
          }}
        >
          <Translate
            translationKey={summaryKey}
            values={{
              subTenantCount: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {formatCount(connectedEntities.subTenants, language)}
                </Box>
              ),
            }}
          />
        </Typography>
      )}
      {keysWithEntities.length > 0 && (
        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            listStyle: "none",
            borderRadius: 1.5,
            border: theme => `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          {keysWithEntities.map(key => (
            <ConnectedEntityRow
              key={key}
              label={t(CONNECTED_ENTITY_LABELS[key])}
              total={connectedEntities[key]}
              units={unitsFor(key)}
              isOpen={openKey === key}
              onToggle={() => setOpenKey(current => (current === key ? null : key))}
            />
          ))}
        </Box>
      )}
      {connectedEntities.sharedRegistrations > 0 && (
        <Box sx={{ mt: 2 }}>
          <Alert>
            {t(
              `OrganisationStructure.DeleteConfirmation.ConnectedEntities.KeptRegistrations.${pluralSuffix(connectedEntities.sharedRegistrations)}`,
              { count: formatCount(connectedEntities.sharedRegistrations, language) }
            )}
          </Alert>
        </Box>
      )}
      {connectedEntities.registrationsLosingDpiaConnection > 0 && (
        <ErrorMessage
          variant="warning"
          errorMessage={t(
            `OrganisationStructure.DeleteConfirmation.ConnectedEntities.LostDpiaConnections.${pluralSuffix(connectedEntities.registrationsLosingDpiaConnection)}`,
            {
              count: connectedEntities.registrationsLosingDpiaConnection,
            }
          )}
        />
      )}
    </Box>
  );
};

const measureScrollbarWidth = (list: HTMLUListElement | null) => {
  list?.style.setProperty("--scrollbar-width", `${list.offsetWidth - list.clientWidth}px`);
};

type ConnectedEntityRowProps = {
  label: string;
  total: number;
  units: Array<{ id: string; name: string; count: number }>;
  isOpen: boolean;
  onToggle: () => void;
};

const ConnectedEntityRow: FC<ConnectedEntityRowProps> = ({ label, total, units, isOpen, onToggle }) => {
  const { language } = useTranslation();
  const listId = useId();

  return (
    <Box
      component="li"
      sx={{ "&:not(:last-of-type)": { borderBottom: theme => `1px solid ${theme.palette.divider}` } }}
    >
      <ButtonBase
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        onClick={onToggle}
        sx={{
          "gap": ROW_GAP,
          "width": "100%",
          "textAlign": "left",
          "pl": ROW_PADDING_LEFT,
          "pr": COUNT_PADDING_RIGHT,
          "py": 1.25,
          "fontFamily": "inherit",
          "fontSize": theme => theme.typography.pxToRem(14),
          "transition": "background-color 150ms ease",
          "&:hover": { backgroundColor: theme => theme.palette.action.hover },
          "&:active": { backgroundColor: theme => theme.palette.action.selected },
          "& > svg": { transition: "transform 200ms ease-out" },
        }}
      >
        <ChevronRightIcon rotate={isOpen ? 90 : 0} size={CHEVRON_SIZE} color={theme => theme.palette.text.secondary} />
        <Box component="span" sx={{ flex: 1 }}>
          {label}
        </Box>
        <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {formatCount(total, language)}
        </Box>
      </ButtonBase>
      <FormDialog.Reveal isOpen={isOpen}>
        <Box
          component="ul"
          id={listId}
          ref={measureScrollbarWidth}
          sx={{
            m: 0,
            pb: 1.5,
            pl: theme => `calc(${theme.spacing(ROW_PADDING_LEFT + ROW_GAP)} + ${CHEVRON_SIZE}px)`,
            pr: theme => `calc(${theme.spacing(COUNT_PADDING_RIGHT)} - var(--scrollbar-width, 0px))`,
            maxHeight: "15rem",
            overflowY: "auto",
            ...customScrollbar,
            listStyle: "none",
            fontSize: theme => theme.typography.pxToRem(13),
          }}
        >
          {units.map(unit => (
            <Box
              component="li"
              key={unit.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.5,
                color: theme => theme.palette.text.secondary,
              }}
            >
              <span>{unit.name}</span>
              <Box
                component="span"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  color: theme => (unit.count === 0 ? theme.palette.text.disabled : undefined),
                }}
              >
                {formatCount(unit.count, language)}
              </Box>
            </Box>
          ))}
        </Box>
      </FormDialog.Reveal>
    </Box>
  );
};
